import { useCallback, useEffect } from 'react';
import { pdfjs } from 'react-pdf';
import { parsePDFSections } from '@/utils/pdfParser';
import { apiUrl } from '@/api/config';

const ensureWorkerReady = () => {
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    const pdfjsVersion = pdfjs.version || '5.4.296';
    pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.min.mjs`;
  }
};

export default function usePdfDocumentLifecycle({
  effectivePdfUrl,
  cachedPdfUrl,
  authToken,
  chapterId,
  prefetchedSections,
  prefetchedNumPages,
  workerReady,
  setWorkerReady,
  setLoading,
  setError,
  setParsing,
  setNumPages,
  setSections,
  setCurrentSectionIndex,
  setCurrentPage,
  setPageScale,
  setZoomLevel,
  setZoomInputValue,
  setManualZoom,
  setIsEditingZoom,
  setPdfDocumentProxy,
  scaleCalculatedRef,
  originalPageWidthRef,
  baseScaleRef,
  pdfDocumentRef,
}) {
  useEffect(() => {
    ensureWorkerReady();
    setWorkerReady(true);
  }, [setWorkerReady]);

  useEffect(() => {
    const loadPDF = async () => {
      if (!effectivePdfUrl) {
        setLoading(false);
        setError(null);
        return;
      }

      if (!workerReady) {
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setParsing(true);

        setNumPages(null);
        setSections([]);
        setCurrentSectionIndex(0);
        setCurrentPage(1);
        setPageScale(1.0);
        setZoomLevel(100);
        setZoomInputValue('100');
        setManualZoom(false);
        setIsEditingZoom(false);

        scaleCalculatedRef.current = false;
        originalPageWidthRef.current = null;
        baseScaleRef.current = 1.0;
        pdfDocumentRef.current = null;
        setPdfDocumentProxy(null);

        const pdfLoadingPromise = pdfjs.getDocument({
          url: effectivePdfUrl,
          httpHeaders: (!cachedPdfUrl && authToken) ? { Authorization: `Bearer ${authToken}` } : {},
          disableAutoFetch: false,
          disableStream: false,
          disableRange: false,
          verbosity: 0,
          maxImageSize: 10 * 1024 * 1024,
          isEvalSupported: false,
          stopAtErrors: false,
          disableAutoFetch: false,
          disableStream: false,
          disableRange: false,
        }).promise;

        let sections = [];
        let numPagesFromCache = null;

        if (Array.isArray(prefetchedSections) && prefetchedSections.length > 0) {
          sections = prefetchedSections;
          numPagesFromCache = prefetchedNumPages ?? null;
        }

        const sectionsPromise = (sections.length === 0 && chapterId) ? (async () => {
          try {
            const response = await fetch(apiUrl(`userportal/superadmin/chapters/${chapterId}/sections/`), {
              headers: {
                Authorization: `Bearer ${authToken}`,
                'Content-Type': 'application/json',
              },
              cache: 'default',
            });

            if (response.ok) {
              const data = await response.json();
              if (data.sections && data.status === 'completed') {
                return { sections: data.sections, numPages: data.numPages };
              }
            }
          } catch {
            // Silently fail - fallback to parser
          }
          return null;
        })() : Promise.resolve(null);

        const [sectionsResult, pdf] = await Promise.all([
          sectionsPromise,
          pdfLoadingPromise.catch((err) => {
            console.error('Error loading PDF document:', err);
            return null;
          })
        ]);

        if (pdf) {
          pdfDocumentRef.current = pdf;
          setPdfDocumentProxy(pdf);
          if (!numPagesFromCache) {
            numPagesFromCache = pdf.numPages;
          }
        }

        if (sectionsResult) {
          sections = sectionsResult.sections;
          numPagesFromCache = sectionsResult.numPages;
        } else if (pdf) {
          numPagesFromCache = pdf.numPages;
          pdfDocumentRef.current = pdf;
          setPdfDocumentProxy(pdf);

          setNumPages(numPagesFromCache);
          setLoading(false);
          setParsing(false);

          parsePDFSections(effectivePdfUrl, authToken).then((result) => {
            if (Array.isArray(result)) {
              setSections(result);
            } else if (result && result.sections) {
              setSections(result.sections);
            }
          }).catch((err) => {
            console.warn('Background section parsing failed:', err);
          });

          return;
        } else {
          const result = await parsePDFSections(effectivePdfUrl, authToken);

          if (Array.isArray(result)) {
            sections = result;
          } else if (result) {
            sections = result.sections || [];
            if (result.numPages) {
              numPagesFromCache = result.numPages;
            }
            if (result.pdfDocument) {
              pdfDocumentRef.current = result.pdfDocument;
              setPdfDocumentProxy(result.pdfDocument);
            }
          }
        }

        setSections(sections);
        if (numPagesFromCache) {
          setNumPages(numPagesFromCache);
        }

        if (sections.length > 0) {
          setCurrentSectionIndex(0);
        }

        setParsing(false);
        setLoading(false);
      } catch (err) {
        console.error('Error loading PDF:', err);
        setError(err.message || 'Failed to load PDF');
        setLoading(false);
        setParsing(false);
      }
    };

    loadPDF();
  }, [
    effectivePdfUrl,
    cachedPdfUrl,
    authToken,
    workerReady,
    chapterId,
    prefetchedSections,
    prefetchedNumPages,
    setLoading,
    setError,
    setParsing,
    setNumPages,
    setSections,
    setCurrentSectionIndex,
    setCurrentPage,
    setPageScale,
    setZoomLevel,
    setZoomInputValue,
    setManualZoom,
    setIsEditingZoom,
    setPdfDocumentProxy,
    scaleCalculatedRef,
    originalPageWidthRef,
    baseScaleRef,
    pdfDocumentRef,
  ]);

  const handleDocumentLoadSuccess = useCallback(({ numPages: docNumPages }) => {
    setNumPages((prevNumPages) => {
      if (!prevNumPages && docNumPages) {
        return docNumPages;
      }
      return prevNumPages;
    });
  }, [setNumPages]);

  return {
    handleDocumentLoadSuccess,
  };
}
